<script lang="ts" setup>
    import type { RuntimeConfig } from 'nuxt/schema';
    import MediceLogo from '~/assets/images/logo.white.svg';
    import GradientCircle from '~/assets/images/gradient-circle-white.svg';
    import AcademyHeroBackgroundAnimated from './AcademyBackgroundAnimated.vue';
    import HeroMedia from '~/components/heroElements/HeroMedia.vue';

    const props = defineProps<{ blok: HeroComponentStoryblok }>();

    const { shopwareDown } = useShopwareDowntime();
    const { refreshSessionContext } = useSessionContext();
    const { displayFirstName, login } = useLoginState();
    const { data: story } = useConfig();
    const { isAuthenticated } = useStoryRolesAuth(story);
    const isAuthenticatedClient = useClientGate(isAuthenticated);
    const bonus = useBonus();
    const { urlSegment } = useLanguage();
    const localePath = useLocalePath();
    const { formatLink } = useInternationalization(localePath);
    const runtimeConfig: RuntimeConfig = useRuntimeConfig();
    const { getPostLoginUrl } = usePostLoginUrl();

    const currentUrl = ref('');
    const { headlineContent, mediaSrc, mediaAlt, mediaContentType } = useHeroComponentContent(() => props.blok);

    const isLoggingIn = ref(false);
    const loginErrors = ref<string[]>([]);

    const invokeLogin = async (): Promise<void> => {
        if (unref(shopwareDown) || isLoggingIn.value) return;

        loginErrors.value = [];
        isLoggingIn.value = true;

        try {
            await login({ redirectTo: await getPostLoginUrl() });
        } catch (e) {
            loginErrors.value = [$t('errors.shopwareLoginError')];
            console.error('[AcademyHero] login failed', e);
        } finally {
            isLoggingIn.value = false;
        }
    };

    const goToRegister = () => {
        log.debug(
            `[AcademyHero] Navigating to registration page: ${runtimeConfig.public.keycloakUrl}/register/?redirect_uri=${currentUrl.value}`
        );
        navigateTo(`${runtimeConfig.public.keycloakUrl}/register/?redirect_uri=${currentUrl.value}`, {
            external: true
        });
    };

    onMounted(async () => {
        currentUrl.value = encodeURIComponent(window.location.href);
        await refreshSessionContext();
    });
</script>

<template>
    <div
        v-editable="props.blok"
        class="hero-container"
    >
        <div class="hero-wrapper">
            <div class="logo-wrapper">
                <img
                    :src="MediceLogo"
                    aria-hidden="true"
                    class="medice-logo"
                />
            </div>

            <div class="content">
                <div v-if="isAuthenticated && isAuthenticatedClient">
                    <div class="headline-container">
                        <h1>
                            <span class="salutation">{{ $t('academy.hero.postLoginClaim') }},</span>
                            <br />
                            <span class="user-name">{{ displayFirstName }}</span>
                        </h1>

                        <div class="badge-container">
                            <div class="loyalty-badge">
                                <span id="points">{{ bonus.currentUsersPoints.formatted }}</span>
                                <span id="label">{{ bonus.currentUsersPoints.unit }}</span>

                                <div class="badge-icon-hex">
                                    <img
                                        src="~/assets/icons/bee.svg"
                                        alt=""
                                    />
                                </div>
                            </div>

                            <div class="cta-container">
                                <p id="motivation">{{ $t('academy.hero.motivation') }}</p>

                                <PrimaryButton
                                    :button-text="$t('academy.hero.postLoginCTA')"
                                    :button-href="formatLink(`/${urlSegment}/shop`)"
                                    has-icon
                                    alternate-styling
                                    size="small"
                                    open-new-tab
                                    space-between
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-else
                    class="headline-container"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="headline"
                        v-html="headlineContent"
                    />
                    <!-- eslint-enable vue/no-v-html -->

                    <div
                        v-if="props.blok.button"
                        class="btn-wrapper"
                    >
                        <div
                            v-if="loginErrors.length"
                            class="login-error"
                            role="alert"
                        >
                            <p
                                v-for="error in loginErrors"
                                :key="error"
                            >
                                {{ error }}
                            </p>
                        </div>

                        <PrimaryButton
                            product-button
                            class="full-width"
                            :button-text="$t('account.signIn')"
                            :disabled="shopwareDown || isLoggingIn"
                            data-testid="hero-login-submit-button"
                            type="button"
                            alternate-styling
                            has-icon
                            space-between
                            @click="invokeLogin"
                        />

                        <PrimaryButton
                            product-button
                            class="full-width"
                            :button-text="$t('account.signUp')"
                            :disabled="shopwareDown"
                            data-testid="hero-login-sign-up-link"
                            has-icon
                            space-between
                            ghost
                            @click="goToRegister"
                        />

                        <StoryblokComponentWrapper
                            v-for="item in props.blok.button"
                            :key="item._uid"
                            :blok="item"
                        />
                    </div>
                </div>
            </div>

            <div class="media-content">
                <HeroMedia
                    :src="mediaSrc"
                    :alt="mediaAlt"
                    :content-type="mediaContentType"
                    storage-key="academy-hero-paused"
                />

                <img
                    :src="GradientCircle"
                    class="gradient-circle"
                    alt=""
                />
            </div>
        </div>

        <HeroScrollDown />
        <AcademyHeroBackgroundAnimated />
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/academy/academy-hero-teaser.scss';
</style>
